/**
 * Who is this user? — one place for every role check, driven by the
 * `account_type` + `organization` the API returns on login and profile.
 *
 * Permission groups no longer decide identity. Use these helpers instead of
 * checking `groups` by name.
 *
 * account_type: uniedu_staff | partner | partner_staff | sub_partner | student
 */

export const PARTNER_TYPES = ["partner", "partner_staff", "sub_partner"];

// There is no is_staff flag: UniEdu staff = account_type "uniedu_staff"
// (superusers are always uniedu_staff too).
export const isUniEduStaff = (p) =>
  !!p && (p.account_type === "uniedu_staff" || p.is_superuser === true);

// UniEdu admins (team role "admin" = superuser). Plain staff and country
// operators are UniEdu staff too but can't use the Partner / Management
// sections — the API enforces the same rule.
export const isUniEduAdmin = (p) => isUniEduStaff(p) && p.is_superuser === true;

// A logged-in applicant owns their org (account_type "partner") from the
// moment they apply, but only counts as a partner once UniEdu approves it.
const hasApprovedOrg = (p) => !!p?.organization?.is_active && !!p?.organization?.is_verified;

export const isPartnerUser = (p) =>
  !!p && !isUniEduStaff(p) && PARTNER_TYPES.includes(p.account_type) && hasApprovedOrg(p);

export const isPartnerOwner = (p) => isPartnerUser(p) && p.account_type === "partner";

/** Applied while logged in, still waiting for approval. */
export const isPendingPartner = (p) =>
  !!p && !isUniEduStaff(p) && PARTNER_TYPES.includes(p.account_type) && !hasApprovedOrg(p);

/** Anyone allowed into /admin/dashboard. */
export const isDashboardUser = (p) => isUniEduStaff(p) || isPartnerUser(p);

/** Everyone else uses the student side of the site. */
export const isStudentUser = (p) => !!p && !isDashboardUser(p);

/** Role keys matched against `roles` on sidebar items (admin.routes.jsx). */
export const sidebarRoles = (p) => {
  if (isUniEduStaff(p)) return ["admin", "agent"];
  if (isPartnerOwner(p)) return ["partner", "partner_owner"];
  if (isPartnerUser(p)) return ["partner"];
  return [];
};

export const roleLabel = (p) => {
  if (!p) return "";
  if (p.is_superuser) return "Super Admin";
  if (isUniEduStaff(p)) return "Administrator";
  if (isPartnerOwner(p)) return "Partner";
  if (isPartnerUser(p)) return p.account_type === "sub_partner" ? "Sub-partner" : "Partner Staff";
  return "Student";
};
